import { ConnectedRouter } from "connected-react-router";
import React, { useMemo } from "react";
import ReactDOM from "react-dom";
import App from "./view/components/App";
import { Provider } from "react-redux";
import { history, store, useAppDispatch } from "./view/store";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { theme } from "./config/theme";
import AuthenticationProxy from "./view/components/auth/authenticationProxy";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./index.scss";
import { useLocation, useRouteMatch } from "react-router";
import { TypeSollicitation } from "./core/apis/monsisra/generated";
import { setType } from "./view/store/expertise/expertise.reducer";
import { UtilsService } from "./core/services/utils.service";
import { verifyAndSelectUser } from "./view/store/auth/auth.action";
import { Loader } from "./view/components/Loader";
import { ConfigService } from "./core/services/config.service";

export const TypeSollicitationCtx = React.createContext(new UtilsService().getDefaultType());
const isRenderedInIframe = window.frameElement !== null;
export const IsFramedCtx = React.createContext(isRenderedInIframe);

function Wrapper() {
	// region get/update typeSollicitation

	const match = useRouteMatch<{ type: "expert" | "ressource" }>("/:type");
	const typeSollicitation = React.useMemo(() => {
		switch (match?.params.type) {
			case "expert":
				return TypeSollicitation.TeleExpertise;
			case "ressource":
				return TypeSollicitation.DemandeAppui;
			default:
				throw new Error(`Bad route: 'expert' or 'ressource' received: ${match?.params.type}`);
		}
	}, [match]);

	const dispatch = useAppDispatch();
	const location = useLocation();
	const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
	const idTechPsCible = useMemo(() => searchParams.get("idTechPsCible") || searchParams.get("IdTechPsCible"), [searchParams]);

	const [isReady, setIsReady] = React.useState(false);

	React.useEffect(() => {
		dispatch(setType(typeSollicitation));
		if (idTechPsCible) {
			let parentUrl: string;
			try {
				parentUrl = window.parent.location.href;
			} catch (error) {
				parentUrl = document.referrer;
				console.error("Error getting parent URL:", error);
			}
			dispatch(verifyAndSelectUser({ idTechPsCible, parentUrl })).then(() => setIsReady(true));
		} else {
			setIsReady(true);
		}
	}, [match, dispatch, typeSollicitation, idTechPsCible]);

	if (!isReady) {
		return <Loader />;
	}
	// endregion

	return (
		<TypeSollicitationCtx.Provider value={typeSollicitation}>
			<App idTechPsCible={idTechPsCible} />
		</TypeSollicitationCtx.Provider>
	);
}

async function render() {
	await ConfigService.getFrontConfig();
	const root = document.getElementById("root");
	ReactDOM.render(
		<React.StrictMode>
			<Provider store={store}>
				<ThemeProvider theme={theme}>
					<ConnectedRouter history={history}>
						<IsFramedCtx.Provider value={isRenderedInIframe}>
							<AuthenticationProxy>
								<Wrapper />
							</AuthenticationProxy>
						</IsFramedCtx.Provider>
					</ConnectedRouter>
					<ToastContainer position={"top-right"} />
					<CssBaseline />
				</ThemeProvider>
			</Provider>
		</React.StrictMode>,
		root
	);
}

void render();
